import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../../Components/common/Navbar";
import { useTranslation } from "react-i18next";

import {
  ArrowRight,
  Award,
  BookOpen,
  BriefcaseBusiness,
  Building2,
  CheckCircle2,
  ClipboardList,
  GraduationCap,
  Laptop2,
  LineChart,
  Network,
  Rocket,
  Settings2,
  Sparkles,
  Target,
  TrendingUp,
  Users,
  WalletCards,
} from "lucide-react";

export default function CorporateTrainingPage() {
  const { t } = useTranslation();

  const trainingSteps = [
    {
      number: "01",
      title: "SHARE YOUR TRAINING REQUIREMENTS",
      description:
        "Tell us about your organization, workforce, skill development needs, and training objectives.",
      icon: ClipboardList,
    },
    {
      number: "02",
      title: "SELECT THE COURSES",
      description:
        "Choose from relevant WiLearn courses and learning programmes based on the requirements of your employees or teams.",
      icon: BookOpen,
    },
    {
      number: "03",
      title: "CUSTOMIZE YOUR TRAINING PLAN",
      description:
        "Develop a structured learning plan based on:",
      icon: Settings2,
    },
    {
      number: "04",
      title: "GET CORPORATE PRICING",
      description:
        "Organizations may receive special corporate pricing and volume-based discounts, subject to the selected programme and number of participants.",
      icon: WalletCards,
    },
    {
      number: "05",
      title: "TRAIN YOUR TEAM",
      description:
        "Provide employees and team members with access to structured online learning and professional development opportunities.",
      icon: Users,
    },
    {
      number: "06",
      title: "TRACK LEARNING PROGRESS",
      description:
        "Monitor participation, learning progress, course completion, and other applicable training activities.",
      icon: LineChart,
    },
    {
      number: "07",
      title: "COMPLETE AND ACHIEVE",
      description:
        "Participants who successfully fulfil the applicable course requirements may receive relevant certificates or completion credentials.",
      icon: Award,
    },
  ];

  const trainingPlanItems = [
    "Number of Participants",
    "Training Requirements",
    "Skills and Competencies",
    "Preferred Learning Areas",
    "Programme Duration",
    "Organizational Objectives",
  ];

  const benefits = [
    {
      icon: Users,
      title: "TRAIN YOUR TEAMS",
      description:
        "Provide structured learning opportunities for employees and professional teams.",
    },
    {
      icon: BookOpen,
      title: "SELECT RELEVANT COURSES",
      description:
        "Choose courses based on your organization's specific training and development needs.",
    },
    {
      icon: Target,
      title: "CUSTOMIZED LEARNING",
      description:
        "Develop training solutions aligned with your workforce requirements and organizational goals.",
    },
    {
      icon: Laptop2,
      title: "FLEXIBLE ONLINE LEARNING",
      description:
        "Enable employees to learn through convenient and accessible digital learning programmes.",
    },
    {
      icon: WalletCards,
      title: "CORPORATE PRICING",
      description:
        "Access special pricing options for organizations and group participants, where applicable.",
    },
    {
      icon: TrendingUp,
      title: "SUPPORT PROFESSIONAL DEVELOPMENT",
      description:
        "Help employees develop new knowledge, skills, and professional competencies.",
    },
  ];

  const trainingAreas = [
    "Leadership and Management",
    "Information Technology",
    "Artificial Intelligence",
    "Digital Skills",
    "Business and Entrepreneurship",
    "Communication Skills",
    "Project Management",
    "Human Resources",
    "Marketing",
    "Finance",
    "Education and Training",
    "Research and Innovation",
    "Professional Development",
  ];

  const applicants = [
    "Companies",
    "Start-ups",
    "Educational Institutions",
    "Non-Profit Organizations",
    "Government and Public Institutions",
    "Training Centres",
    "Professional Associations",
    "Small and Medium Enterprises",
    "Corporate Teams",
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar page="corporate-training" />

      {/* =====================================================
          HERO
      ====================================================== */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-violet-950 to-purple-900">
        <div className="absolute inset-0">
          <div className="absolute -left-24 top-0 h-96 w-96 rounded-full bg-pink-500/20 blur-3xl" />
          <div className="absolute right-0 top-10 h-96 w-96 rounded-full bg-violet-500/20 blur-3xl" />
          <div className="absolute bottom-0 left-1/2 h-72 w-72 rounded-full bg-purple-400/10 blur-3xl" />

          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:40px_40px]" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <div className="mx-auto max-w-6xl text-center">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-semibold text-violet-100 backdrop-blur-md">
              <Building2 className="h-4 w-4" />
              {t("WiLearn Corporate Training")}
            </div>

            <h1 className="text-4xl font-black leading-tight text-white sm:text-5xl lg:text-6xl">
              {t("WiLearn Corporate Training")}
            </h1>

            <p className="mt-5 text-lg font-bold uppercase tracking-[0.18em] text-pink-300 sm:text-xl">
              {t("EMPOWERING PEOPLE. STRENGTHENING ORGANIZATIONS.")}
            </p>

            <p className="mx-auto mt-7 max-w-5xl text-justify text-lg leading-8 text-slate-200 sm:text-xl">
              {t(
                "WiLearn Corporate Training provides flexible and customized learning solutions designed to help organizations develop employee skills, strengthen professional capabilities, and support organizational growth."
              )}
            </p>

            <p className="mx-auto mt-4 max-w-5xl text-justify text-base leading-8 text-slate-300 sm:text-lg">
              {t(
                "Our training programmes can be tailored to meet the specific learning and development requirements of companies, institutions, organizations, and professional teams."
              )}
            </p>

            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <a
                href="#proposal"
                className="inline-flex items-center gap-2 rounded-full bg-pink-500 px-7 py-3 font-bold text-white shadow-lg shadow-pink-950/30 transition duration-300 hover:-translate-y-1 hover:bg-pink-600"
              >
                {t("Submit Your Proposal")}
                <ArrowRight className="h-4 w-4" />
              </a>

              <a
                href="#training"
                className="rounded-full border border-white/30 bg-white/10 px-7 py-3 font-semibold text-white backdrop-blur-md transition hover:bg-white/20"
              >
                {t("Explore Corporate Training")}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          TRANSFORM YOUR WORKFORCE
      ====================================================== */}
      <section
        id="training"
        className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8"
      >
        <div className="grid gap-10 lg:grid-cols-[1fr_0.9fr] lg:items-center">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-pink-600">
              {t("TRANSFORM YOUR WORKFORCE")}
            </p>

            <h2 className="mt-3 max-w-3xl text-3xl font-bold leading-tight text-slate-900 sm:text-4xl">
              {t("Build stronger teams through continuous learning")}
            </h2>

            <p className="mt-6 text-justify text-lg leading-8 text-slate-600">
              {t(
                "Invest in continuous learning and professional development with structured training programmes designed for today's evolving workplace."
              )}
            </p>

            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              <div className="rounded-2xl border border-violet-100 bg-white p-5 shadow-sm">
                <Laptop2 className="h-7 w-7 text-violet-700" />

                {/* <p className="mt-4 text-sm font-semibold text-slate-500">
                  {t("Flexible Learning Solutions")}
                </p> */}

                

                <p className="mt-2 font-bold leading-6 text-slate-900">
                  {t("Flexible Learning Solutions")}
                </p>
              </div>

              <div className="rounded-2xl border border-violet-100 bg-white p-5 shadow-sm">
                <Building2 className="h-7 w-7 text-violet-700" />
                <p className="mt-2 font-bold leading-6 text-slate-900">
                  {t("Online | Virtual | Customized Training")}
                </p>
                {/* <p className="mt-4 text-sm font-semibold text-slate-500">
                  {t("Training Designed for Organizations")}
                </p> */}

                {/* <p className="mt-2 font-bold leading-6 text-slate-900">
                  {t("Purpose-built learning solutions")}
                </p> */}
              </div>

              <div className="rounded-2xl border border-violet-100 bg-white p-5 shadow-sm">
                <BookOpen className="h-7 w-7 text-violet-700" />
                <p className="mt-2 font-bold leading-6 text-slate-900">
                  {t("Training Designed for Organizations")}
                </p>
{/* 
                <p className="mt-4 text-sm font-semibold text-slate-500">
                  {t("Relevant Learning")}
                </p>

                <p className="mt-2 font-bold leading-6 text-slate-900">
                  {t("Courses based on organizational needs")}
                </p> */}
              </div>
            </div>

            <p className="mt-7 text-justify text-lg leading-8 text-slate-600">
              {t(
                "Select relevant courses and learning programmes based on your organization's requirements."
              )}
            </p>
          </div>

          <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-violet-700 via-purple-700 to-pink-600 p-8 text-white shadow-2xl sm:p-10">
            <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10" />
            <div className="absolute -bottom-14 -left-14 h-52 w-52 rounded-full bg-white/10" />

            <div className="relative">
              <Network className="h-12 w-12 text-pink-200" />

              <h3 className="mt-6 text-3xl font-bold">
                {t("Learning aligned with your organization")}
              </h3>

              <p className="mt-5 text-justify text-lg leading-8 text-violet-100">
                {t(
                  "Create learning programmes that support workforce development, organizational priorities, and professional growth."
                )}
              </p>

              <div className="mt-8 grid grid-cols-2 gap-3">
                {[
                  "Custom Learning",
                  "Professional Skills",
                  "Team Development",
                  "Flexible Delivery",
                ].map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-2 rounded-xl bg-white/10 px-3 py-3 text-sm font-semibold backdrop-blur"
                  >
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-pink-200" />
                    {t(item)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          HOW CORPORATE TRAINING WORKS
      ====================================================== */}
      <section className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <p className="text-sm font-bold uppercase tracking-[0.25em] text-violet-600">
              {t("HOW CORPORATE TRAINING WORKS")}
            </p>

            <h2 className="mt-3 text-3xl font-bold text-slate-900 sm:text-4xl">
              {t("A structured approach to workforce development")}
            </h2>
          </div>

          <div className="mt-12 space-y-5">
            {trainingSteps.map((step, index) => {
              const Icon = step.icon;

              return (
                <div
                  key={step.number}
                  className="group grid gap-5 rounded-3xl border border-slate-200 bg-slate-50 p-6 transition duration-300 hover:-translate-y-1 hover:border-pink-200 hover:bg-white hover:shadow-xl md:grid-cols-[90px_1fr]"
                >
                  <div>
                    <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-100 to-pink-100 text-violet-700 transition duration-300 group-hover:bg-gradient-to-br group-hover:from-violet-600 group-hover:to-pink-500 group-hover:text-white">
                      <Icon className="h-7 w-7" />
                    </div>
                  </div>

                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="text-sm font-black tracking-widest text-pink-500">
                        {step.number}
                      </span>

                      <h3 className="text-xl font-bold text-slate-900">
                        {t(step.title)}
                      </h3>
                    </div>

                    <p className="mt-3 text-justify text-base leading-7 text-slate-600">
                      {t(step.description)}
                    </p>

                    {index === 2 && (
                      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {trainingPlanItems.map((item) => (
                          <div
                            key={item}
                            className="flex items-start gap-3 rounded-xl border border-violet-100 bg-white p-4"
                          >
                            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-pink-500" />

                            <span className="text-sm font-semibold leading-5 text-slate-700">
                              {t(item)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* =====================================================
          WHY CHOOSE WILEARN CORPORATE TRAINING
      ====================================================== */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-sm font-bold uppercase tracking-[0.25em] text-pink-600">
            {t("WHY CHOOSE WILEARN CORPORATE TRAINING?")}
          </p>

          <h2 className="mt-3 text-3xl font-bold text-slate-900 sm:text-4xl">
            {t("Learning solutions built for modern organizations")}
          </h2>
        </div>

        <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {benefits.map((benefit) => {
            const Icon = benefit.icon;

            return (
              <div
                key={benefit.title}
                className="group rounded-3xl border border-slate-200 bg-white p-7 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-pink-200 hover:shadow-xl"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-100 to-pink-100 text-violet-700 transition duration-300 group-hover:scale-105">
                  <Icon className="h-7 w-7" />
                </div>

                <h3 className="mt-5 text-xl font-bold text-slate-900">
                  {t(benefit.title)}
                </h3>

                <p className="mt-3 text-justify leading-7 text-slate-600">
                  {t(benefit.description)}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* =====================================================
          TRAINING AREAS
      ====================================================== */}
      <section className="bg-slate-950 py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.25em] text-pink-300">
                {t("TRAINING AREAS")}
              </p>

              <h2 className="mt-3 text-3xl font-bold leading-tight text-white sm:text-4xl">
                {t("Develop skills across diverse professional areas")}
              </h2>

              <p className="mt-6 text-justify text-lg leading-8 text-slate-300">
                {t(
                  "WiLearn Corporate Training can support learning and development in areas such as:"
                )}
              </p>

              <p className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-5 text-justify text-sm italic leading-7 text-slate-400">
                {t(
                  "Training areas may vary based on available courses and organizational requirements."
                )}
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {trainingAreas.map((area, index) => (
                <div
                  key={area}
                  className="group flex items-center gap-4 rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur transition duration-300 hover:border-pink-400/40 hover:bg-white/10"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-violet-500/20 text-pink-300">
                    <span className="text-sm font-black">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                  </div>

                  <span className="font-semibold leading-6 text-slate-100">
                    {t(area)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          WHO CAN APPLY
      ====================================================== */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="rounded-[2rem] border border-violet-100 bg-gradient-to-br from-white via-violet-50 to-pink-50 p-7 sm:p-10 lg:p-12">
          <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-center">
            <div>
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-100 text-violet-700">
                <Building2 className="h-7 w-7" />
              </div>

              <p className="mt-6 text-sm font-bold uppercase tracking-[0.25em] text-violet-600">
                {t("WHO CAN APPLY?")}
              </p>

              <h2 className="mt-3 text-3xl font-bold text-slate-900 sm:text-4xl">
                {t("Corporate learning for organizations of every size")}
              </h2>

              <p className="mt-5 text-justify text-lg leading-8 text-slate-600">
                {t("WiLearn Corporate Training is suitable for:")}
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              {applicants.map((item) => (
                <div
                  key={item}
                  className="flex items-center gap-3 rounded-2xl border border-white bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-100 to-pink-100">
                    <CheckCircle2 className="h-5 w-5 text-violet-700" />
                  </div>

                  <span className="font-semibold leading-6 text-slate-800">
                    {t(item)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================
          SUBMIT YOUR PROPOSAL
      ====================================================== */}
      <section id="proposal" className="bg-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-r from-violet-700 via-purple-700 to-pink-600 px-6 py-12 shadow-2xl sm:px-10 lg:px-14">
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/10 blur-2xl" />
            <div className="absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-pink-300/10 blur-2xl" />

            <div className="relative grid gap-10 lg:grid-cols-[1fr_0.8fr] lg:items-center">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.3em] text-pink-200">
                  {t("SUBMIT YOUR PROPOSAL")}
                </p>

                <h2 className="mt-4 text-3xl font-black text-white sm:text-4xl">
                  {t("BUILD A FUTURE-READY WORKFORCE")}
                </h2>

                <h3 className="mt-3 text-2xl font-bold text-pink-100">
                  {t("TRAIN. DEVELOP. GROW.")}
                </h3>

                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  {[
                    "Flexible Learning Solutions",
                    "Customized Training Plans",
                    "Relevant Professional Courses",
                    "Corporate and Group Pricing",
                  ].map((item) => (
                    <div
                      key={item}
                      className="flex items-center gap-3 rounded-xl border border-white/15 bg-white/10 px-4 py-4 text-white backdrop-blur"
                    >
                      <CheckCircle2 className="h-5 w-5 shrink-0 text-pink-200" />
                      <span className="font-semibold">{t(item)}</span>
                    </div>
                  ))}
                </div>

                <p className="mt-8 max-w-3xl text-lg font-bold uppercase leading-8 tracking-wide text-white">
                  {t(
                    "CONNECT WITH WILEARN TO DEVELOP A TRAINING PROGRAMME FOR YOUR ORGANIZATION."
                  )}
                </p>
              </div>

              <div className="rounded-3xl border border-white/20 bg-white/10 p-7 text-center backdrop-blur-xl sm:p-9">
                <BriefcaseBusiness className="mx-auto h-14 w-14 text-pink-200" />

                <h3 className="mt-5 text-2xl font-bold text-white">
                  {t("Plan your organization's training programme")}
                </h3>

                <p className="mt-4 text-justify leading-7 text-violet-100">
                  {t(
                    "Connect with WiLearn to discuss your workforce requirements, preferred learning areas, participant count, and training objectives."
                  )}
                </p>

                <Link
                  to="/contact"
                  className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-white px-6 py-4 font-bold text-violet-700 shadow-lg transition duration-300 hover:-translate-y-1 hover:bg-violet-50"
                >
                  {t("Submit Your Proposal")}
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}